import { useSmoothRevealCursor } from "./smooth-reveal-engine";

/**
 * `useChat` `experimental_throttle` (ms) for the Alby chat surfaces. Part of the
 * same tuning set as the reveal cadence constants: it caps how often streamed
 * `messages` updates flow into React, keeping the reveal's target buffer fresh
 * without over-rendering. The smooth reveal decouples the visual pacing from
 * this, so it only needs to be a bit tighter than the reveal cadence — not so
 * low it multiplies upstream re-renders. Shared so both surfaces stay in sync.
 */
export const STREAM_THROTTLE_MS = 30;

/**
 * Reveal `fullText` as a steady character stream while a turn is streaming,
 * decoupling the visual reveal from network chunk arrival. A thin adapter over
 * the internal {@link useSmoothRevealCursor} engine — a single-segment reveal
 * (target === total, seed 0, prompt at the global start).
 *
 * The AI SDK delivers tokens in bursts (further coalesced by `useChat`'s
 * `experimental_throttle`), so rendering `part.text` directly steps ≤20×/sec and
 * reads as chunky; the engine's velocity-smoothed tracker instead moves at a
 * near-constant speed that ignores per-chunk jitter.
 *
 * @param fullText   The full (possibly still-growing) text to reveal.
 * @param isStreaming Whether this text belongs to the actively-streaming turn.
 * @returns The prefix of `fullText` that should be displayed right now.
 */
export const useSmoothStreamedText = (fullText: string, isStreaming: boolean): string => {
  const cursor = useSmoothRevealCursor({
    active: isStreaming,
    target: fullText.length,
    total: fullText.length,
    initialCursor: 0,
  });
  return fullText.slice(0, Math.min(cursor, fullText.length));
};
